import { ROUTE_SEARCH } from "@/constants/routes.constants";
import { CommandGroup, CommandSeparator } from "@/shadcn/components/ui/command";

import AddFiltersSearch from "@/components/AddFiltersSearch/AddFiltersSearch.component";
import CollaboratorPicker from "@/components/CollaboratorPicker/collaboratorPicker.component";
import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import DatePickerSearch from "@/components/DatePickerSearch/datePickerSearch.component";
import MultiCheckbox from "@/components/MultiCheckbox/multiCheckbox.component";
import MultiPicker from "@/components/PersonPickerDropdown/multiPicker";
import SearchBoxFilterInverseCheckbox from "@/components/SearchContainer/searchBoxFilterInverseCheckbox.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { useUser } from "@/hooks/AppContext/user.hook";
import { Button } from "@/shadcn/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { Input } from "@/shadcn/components/ui/input";
import {
  AddFilterDateType,
  AddFilterDateTypeSchema,
  AddFilterOptions,
  AddFilterPeopleType,
  AddFilterPeopleTypeSchema,
  Collaborator,
  DateRangeSearch,
  DateType,
  DateTypeDataSchema,
  DateTypeSchema,
  NewTaskTag,
  SEARCH_COLLABORATOR_TYPES,
  SEARCH_TAG_TYPES,
  SearchParamInverseFilter,
  SearchParamsObj,
  WithinDate,
} from "@/types";
import { validateWithSchema } from "@/utils/dataValidators.zod";
import {
  locForSetRecentSearches,
  LSClearSearchParams,
  LSgetSearchParams,
  LSsetSearchParams,
} from "@/utils/localForageUtils";
import { formatDueDate } from "@/utils/utils";
import { Cross2Icon } from "@radix-ui/react-icons";
import { ChevronsUpDown } from "lucide-react";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

type SearchBoxFilterAdvancedProps = {
  advFilterRef: React.MutableRefObject<HTMLDivElement | null>;
  statusRef: React.MutableRefObject<HTMLDivElement | null>;
  multiAssigneeRef: React.MutableRefObject<HTMLDivElement | null>;
  dateDropdownRef: React.MutableRefObject<HTMLDivElement | null>;
  collaboratorRef: React.MutableRefObject<HTMLDivElement | null>;
  buttonSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  addFilterRef: React.MutableRefObject<HTMLDivElement | null>;
  popOverContentRef: React.MutableRefObject<HTMLDivElement | null>;
  filtering: boolean;
  inputValue: string;
};

const SearchBoxFilterAdvanced: React.FC<SearchBoxFilterAdvancedProps> = ({
  advFilterRef,
  statusRef,
  multiAssigneeRef,
  dateDropdownRef,
  collaboratorRef,
  buttonSectionRef,
  addFilterRef,
  popOverContentRef,
  filtering,
  inputValue,
}) => {
  const { getUserById } = useUser();
  const nav = useNavigate();
  const location = useLocation();
  const isNotSmall = useMediaQuery("sm");

  const [assignedTo, setAssignedTo] = useState<number[]>([]);

  const [collaboratorInput, setCollaboratorInput] = useState<Collaborator[]>(
    []
  );
  const [collaboratorSearchType, setCollaboratorSearchType] =
    useState<SearchParamsObj["collaboratorSearchType"]>("exact");
  const [title, setTitle] = useState<string>("");
  const [status, setStatus] = useState<
    // "complete" | "incomplete" | "all" | null
    SearchParamsObj["status"]
  >(null);
  const [tagData, setTagData] = useState<NewTaskTag[]>([]);
  const [tagType, setTagType] = useState<SearchParamsObj["tagType"]>("exact");

  // Additional Filter States
  const [addedFilters, setAddedFilters] = useState<AddFilterOptions>({
    date: null,
    people: null,
  });

  // Due date
  const [dueDateType, setDueDateType] = useState<DateType>(null);
  const [dueDateData, setDueDateData] = useState<
    WithinDate | DateRangeSearch | Date | string | null
  >(null);

  // Created date
  const [createdDateType, setCreatedDateType] = useState<DateType>(null);
  const [createdDateData, setCreatedDateData] = useState<
    WithinDate | DateRangeSearch | Date | string | null
  >(null);

  // Updated date
  const [completedDateType, setCompletedDateType] = useState<DateType>(null);
  const [completedDateData, setCompletedDateData] = useState<
    WithinDate | DateRangeSearch | Date | string | null
  >(null);

  // Modified date
  const [modifiedDateType, setModifiedDateType] = useState<DateType>(null);
  const [modifiedDateData, setModifiedDateData] = useState<
    WithinDate | DateRangeSearch | Date | string | null
  >(null);

  // People
  const [assignedBy, setAssignedBy] = useState<number[]>([]);
  const [createdBy, setCreatedBy] = useState<number[]>([]);
  const [updatedBy, setUpdatedBy] = useState<number[]>([]);
  const [hasTimer, setHasTimer] = useState<number[]>([]);

  const [queryString, setQueryString] = useState<string>("");
  const [inverseFilters, setInverseFilters] = useState<
    SearchParamsObj["inverseFilters"]
  >([]);

  // #region Search Input Handling
  function handleCollaboratorChange(collaboratorID: number) {
    setCollaboratorInput((prev) => {
      if (!prev) return [];

      const collaboratorExists = prev.some(
        (collaborator) => collaborator.id === collaboratorID
      );

      const updatedCollaborators = collaboratorExists
        ? prev.filter((collaborator) => collaborator.id !== collaboratorID)
        : [
            ...prev,
            {
              id: collaboratorID,
              full_name: `${getUserById(collaboratorID)?.first_name ?? ""} ${
                getUserById(collaboratorID)?.surname ?? ""
              }`,
              email: getUserById(collaboratorID)?.email ?? "",
            },
          ];

      return updatedCollaborators;
    });
  }

  const handleAssigneeChange = (
    assigneeID: number,
    setAssigneeState: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    setAssigneeState((prev) => {
      if (!prev) return [];

      const assigneeExists = prev.includes(assigneeID);

      const updatedAssignees = assigneeExists
        ? prev.filter((assignee) => assignee !== assigneeID)
        : [...prev, assigneeID];

      return updatedAssignees;
    });
  };

  const handleDateTypeChange = (
    type: DateType,
    data: WithinDate | DateRangeSearch | Date | string | null,
    setDateType: React.Dispatch<React.SetStateAction<DateType>>,
    setDateData: React.Dispatch<
      React.SetStateAction<WithinDate | DateRangeSearch | Date | string | null>
    >
  ) => {
    console.log("type", type);
    console.log("data", data);
    const result = DateTypeDataSchema.safeParse({ type, data });

    if (!result.success) {
      console.error("Invalid data structure", result.error);
      return;
    }

    if (type === "Within the last" || type === "Within the next") {
      const { timePeriods, timeUnit } = data as WithinDate;
      console.log("data", data);
      console.log("type", type);
      setDateType(type);
      setDateData({ timePeriods, timeUnit });
    }

    if (type === "Date Range") {
      const { fromDate, toDate } = data as DateRangeSearch;
      console.log("toDate", toDate);

      console.log("formattedFrom", fromDate);
      console.log("formattedTo", toDate);
      setDateType(type);
      setDateData({ fromDate, toDate });
    }

    if (type === "Specific Date") {
      setDateType(type);
      const formattedDate = formatDueDate(data as Date);
      console.log("formattedDate", formattedDate);
      setDateData(formattedDate);
    }

    if (
      type === "Any Date" ||
      type === "Yesterday" ||
      type === "Today" ||
      type === "Tomorrow"
    ) {
      setDateType(type);
      setDateData(null);
    }
  };

  const handleTagChange = (tag: NewTaskTag) => {
    console.log("New Tag: ", tag);

    setTagData((prev) => {
      if (!prev) return [];

      const tagExists = prev.some(
        (t) => t.tag === tag.tag && t.category_id === tag.category_id
      );

      const updatedTags = tagExists
        ? prev.filter(
            (t) => !(t.tag === tag.tag && t.category_id === tag.category_id)
          )
        : [...prev, tag];

      console.log("Updated Tags: ", updatedTags);
      return updatedTags;
    });
  };

  // Update query string when search inputs change
  useEffect(() => {
    const queryParams = new URLSearchParams();

    // Clear all query params
    for (const key of queryParams.keys()) {
      queryParams.delete(key);
    }

    if (assignedTo.length > 0)
      queryParams.append("assignedTo", assignedTo.toString());
    if (collaboratorInput.length > 0) {
      queryParams.append(
        "collaborators",
        collaboratorInput.map((c) => c.id).join(",")
      );
      if (collaboratorSearchType) {
        queryParams.append("collaboratorType", collaboratorSearchType);
      }
    }

    if (title) queryParams.append("title", title);
    if (status) queryParams.append("status", status);
    if (dueDateType) queryParams.append("dueDateType", dueDateType);
    if (dueDateData)
      queryParams.append("dueDateData", JSON.stringify(dueDateData));
    if (tagData.length > 0) {
      queryParams.append("tagData", tagData.map((t) => t.tag).join(","));
      if (tagType) {
        queryParams.append("tagType", tagType);
      }
    }
    if (createdDateType) {
      queryParams.append("createdDateType", createdDateType);
    }
    if (createdDateData) {
      queryParams.append("createdDateData", JSON.stringify(createdDateData));
    }
    if (completedDateType) {
      queryParams.append("completedDateType", completedDateType);
    }
    if (completedDateData) {
      queryParams.append(
        "completedDateData",
        JSON.stringify(completedDateData)
      );
    }
    if (modifiedDateType) {
      queryParams.append("modifiedDateType", modifiedDateType);
    }
    if (modifiedDateData) {
      queryParams.append("modifiedDateData", JSON.stringify(modifiedDateData));
    }

    if (assignedBy.length > 0) {
      queryParams.append("assignedBy", assignedBy.toString());
    }
    if (createdBy.length > 0) {
      queryParams.append("createdBy", createdBy.toString());
    }
    if (updatedBy.length > 0) {
      queryParams.append("updatedBy", updatedBy.toString());
    }
    if (hasTimer.length > 0) {
      queryParams.append("hasTimer", hasTimer.toString());
    }
    if (inverseFilters.length > 0) {
      queryParams.append("inverseFilters", inverseFilters.join(","));
    }

    // Update LS for search params
    LSsetSearchParams({
      assignedTo: assignedTo,
      collaboratorInput: collaboratorInput,
      title: title,
      status: status,
      collaboratorSearchType: collaboratorSearchType,
      dueDateType: dueDateType,
      dueDateData: dueDateData,
      tagData: tagData,
      tagType: tagType,
      createdDateType: createdDateType,
      createdDateData: createdDateData,
      completedDateType: completedDateType,
      completedDateData: completedDateData,
      modifiedDateType: modifiedDateType,
      modifiedDateData: modifiedDateData,
      assignedBy: assignedBy,
      createdBy: createdBy,
      updatedBy: updatedBy,
      hasTimer: hasTimer,
      inverseFilters: inverseFilters,
    });
    // console.log("dateData", dueDateData);
    setQueryString(queryParams.toString());
  }, [
    assignedTo,
    title,
    status,
    collaboratorSearchType,
    dueDateType,
    dueDateData,
    collaboratorInput,
    tagData,
    tagType,
    createdDateType,
    createdDateData,
    completedDateType,
    completedDateData,
    modifiedDateType,
    modifiedDateData,
    addedFilters,
    assignedBy,
    createdBy,
    updatedBy,
    hasTimer,
    inverseFilters,
  ]);

  // Update Additional filters, for first render display + updating
  function updateDateFilters(
    setAddedFilters: React.Dispatch<React.SetStateAction<AddFilterOptions>>,
    filter: AddFilterDateType,
    setting: boolean
  ) {
    console.log("filter", filter);
    setAddedFilters((prev) => {
      const dateArray = Array.isArray(prev.date) ? prev.date : [];
      let updatedDateArray;
      if (setting) {
        updatedDateArray = dateArray.includes(filter)
          ? dateArray
          : [...dateArray, filter];
      } else {
        updatedDateArray = dateArray.includes(filter)
          ? dateArray.filter((item) => item !== filter)
          : [...dateArray, filter];
      }
      console.log("dateArray", dateArray);
      console.log("updateDateArray", updatedDateArray);
      return {
        ...prev,
        date: updatedDateArray,
      };
    });
  }

  // If the filter (from Add filter) is removed, remove the corresponding state
  useEffect(() => {
    // People
    if (addedFilters.people && !addedFilters.people.includes("Assigned by")) {
      setAssignedBy([]);
    }
    if (addedFilters.people && !addedFilters.people.includes("Created By")) {
      setCreatedBy([]);
    }
    if (addedFilters.people && !addedFilters.people.includes("Updated By")) {
      setUpdatedBy([]);
    }
    if (addedFilters.people && !addedFilters.people.includes("Has Timer")) {
      setHasTimer([]);
    }

    // Dates
    if (
      addedFilters.date &&
      !addedFilters.date.includes("Created Date") &&
      createdDateType
    ) {
      setCreatedDateType(null);
      setCreatedDateData(null);
    }
    if (
      addedFilters.date &&
      !addedFilters.date.includes("Completed Date") &&
      completedDateType
    ) {
      setCompletedDateType(null);
      setCompletedDateData(null);
    }
    if (
      addedFilters.date &&
      !addedFilters.date.includes("Last Modified") &&
      modifiedDateType
    ) {
      setModifiedDateType(null);
      setModifiedDateData(null);
    }
  }, [addedFilters, completedDateType, createdDateType, modifiedDateType]);

  function updatePeopleFilters(
    setAddedFilters: React.Dispatch<React.SetStateAction<AddFilterOptions>>,
    filter: AddFilterPeopleType,
    setting: boolean
  ) {
    console.log("filter", filter);
    setAddedFilters((prev) => {
      const peopleArray = Array.isArray(prev.people) ? prev.people : [];
      let updatedPeopleArray;
      if (setting) {
        updatedPeopleArray = peopleArray.includes(filter)
          ? peopleArray
          : [...peopleArray, filter];
      } else {
        updatedPeopleArray = peopleArray.includes(filter)
          ? peopleArray.filter((item) => item !== filter)
          : [...peopleArray, filter];
      }
      console.log("peopleArray", peopleArray);
      console.log("updatedPeopleArray", updatedPeopleArray);
      return {
        ...prev,
        people: updatedPeopleArray,
      };
    });
  }

  const handleAddFilter = (
    target: "date" | "people",
    filter: AddFilterDateType | AddFilterPeopleType
  ) => {
    console.log("target", target);
    console.log("filter", filter);
    if (target === "date") {
      const validateDateFilter = validateWithSchema(
        filter,
        AddFilterDateTypeSchema,
        "Date Filter"
      );
      if (validateDateFilter) {
        updateDateFilters(setAddedFilters, validateDateFilter, false);
      }
    }

    if (target === "people") {
      const validatePeopleFilter = validateWithSchema(
        filter,
        AddFilterPeopleTypeSchema,
        "People Filter"
      );
      console.log("validatePeopleFilter", validatePeopleFilter);
      if (validatePeopleFilter) {
        updatePeopleFilters(setAddedFilters, validatePeopleFilter, false);
      }
    }
  };

  // Clear states
  function clearSearch() {
    console.log("triggered");
    setAssignedTo([]);
    setCollaboratorInput([]);
    setTitle("");
    setStatus(null);
    setCollaboratorSearchType("exact");
    setDueDateType(null);
    setDueDateData(null);
    setTagData([]);
    setTagType("exact");

    // Added
    setAddedFilters({
      date: null,
      people: null,
    });

    // Additional Dates
    setCreatedDateType(null);
    setCreatedDateData(null);
    setCompletedDateType(null);
    setCompletedDateData(null);
    setModifiedDateType(null);
    setModifiedDateData(null);

    // Additional People
    setAssignedBy([]);
    setCreatedBy([]);
    setUpdatedBy([]);
    setHasTimer([]);

    setInverseFilters([]);

    // Clear query string
    setQueryString("");
    LSClearSearchParams();
    const queryParams = new URLSearchParams();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`
    );
  }

  // Check LS for search params, for persisting search inputs
  useEffect(() => {
    async function handleLSFetch() {
      const lsParams = await LSgetSearchParams();
      console.log("lsParams", lsParams);
      setAssignedTo(
        lsParams.assignedTo.filter((id): id is number => id !== null)
      );
      setCollaboratorInput(lsParams.collaboratorInput);
      setTitle(lsParams.title ?? "");

      // Due Date
      if (lsParams.dueDateType) {
        // Validate Date Type
        const validatedDueDateType = validateWithSchema(
          lsParams.dueDateType,
          DateTypeSchema,
          "DateType"
        );
        if (validatedDueDateType) {
          setDueDateType(validatedDueDateType);
        } else {
          setDueDateType(null);
        }
      }
      setDueDateData(lsParams.dueDateData ?? null);

      // Status
      if (lsParams.status) {
        setStatus(lsParams.status);
      } else {
        setStatus(null);
      }

      // Tags
      if (lsParams.tagData) {
        setTagData(lsParams.tagData);
        if (
          lsParams.tagType === "exact" ||
          lsParams.tagType === "partial" ||
          lsParams.tagType === "any"
        ) {
          setTagType(lsParams.tagType);
        }
      }

      // Additional Filters
      // Created Date Type
      if (lsParams.createdDateType) {
        const validatedCreatedDateType = validateWithSchema(
          lsParams.createdDateType,
          DateTypeSchema,
          "Created Date Type"
        );
        if (validatedCreatedDateType) {
          setCreatedDateType(validatedCreatedDateType);
        } else {
          setCreatedDateType(null);
        }
      }

      // Created Date Data
      if (lsParams.createdDateData) {
        setCreatedDateData(lsParams.createdDateData);

        // Persisting filters
        const validateCreatedDateData = validateWithSchema(
          "Created Date",
          AddFilterDateTypeSchema,
          "Created Date Data"
        );
        if (validateCreatedDateData) {
          updateDateFilters(setAddedFilters, validateCreatedDateData, true);
        }
      } else {
        setCreatedDateData(null);
      }

      // Completed Date Type
      if (lsParams.completedDateType) {
        // Validate Date Type
        const validatedCompletedDateType = validateWithSchema(
          lsParams.completedDateType,
          DateTypeSchema,
          "Completed Date Type"
        );

        if (validatedCompletedDateType) {
          setCompletedDateType(validatedCompletedDateType);
        } else {
          setCompletedDateType(null);
        }
      }

      // Completed Date Data
      if (lsParams.completedDateData) {
        setCompletedDateData(lsParams.completedDateData);

        // Persisting filters
        const validateCompletedDateData = validateWithSchema(
          "Last Modified",
          AddFilterDateTypeSchema,
          "Completed Date Data"
        );
        if (validateCompletedDateData) {
          updateDateFilters(setAddedFilters, validateCompletedDateData, true);
        }
      } else {
        setCompletedDateData(null);
      }

      // Modified Date Type
      if (lsParams.modifiedDateType) {
        // Validate Date Type
        const validatedModifiedDateType = validateWithSchema(
          lsParams.modifiedDateType,
          DateTypeSchema,
          "Modified Date Type"
        );

        if (validatedModifiedDateType) {
          setModifiedDateType(validatedModifiedDateType);
        } else {
          setModifiedDateType(null);
        }
      }

      // Modified Date Data
      if (lsParams.modifiedDateData) {
        setModifiedDateData(lsParams.modifiedDateData);

        // Persisting filters
        const validateModifiedDateData = validateWithSchema(
          "Last Modified",
          AddFilterDateTypeSchema,
          "Modified Date Data"
        );
        if (validateModifiedDateData) {
          updateDateFilters(setAddedFilters, validateModifiedDateData, true);
        }
      } else {
        setModifiedDateData(null);
      }

      // Assigned By
      if (lsParams.assignedBy.length > 0) {
        setAssignedBy(
          lsParams.assignedBy.filter((id): id is number => id !== null)
        );
        updatePeopleFilters(setAddedFilters, "Assigned by", true);
      }

      // Created By
      if (lsParams.createdBy.length > 0) {
        setCreatedBy(
          lsParams.createdBy.filter((id): id is number => id !== null)
        );
        updatePeopleFilters(setAddedFilters, "Created By", true);
      }

      // Updated By
      if (lsParams.updatedBy.length > 0) {
        setUpdatedBy(
          lsParams.updatedBy.filter((id): id is number => id !== null)
        );
        updatePeopleFilters(setAddedFilters, "Updated By", true);
      }

      // Has Timer
      if (lsParams.hasTimer.length > 0) {
        setHasTimer(
          lsParams.hasTimer.filter((id): id is number => id !== null)
        );
        updatePeopleFilters(setAddedFilters, "Has Timer", true);
      }

      if (lsParams.inverseFilters) {
        setInverseFilters(lsParams.inverseFilters);
      }
    }
    handleLSFetch();
  }, [location.search]);

  const handleSearch = useCallback(async () => {
    // Only search if popover content is present
    if (!popOverContentRef.current) {
      return;
    }

    let searchUrl = "";
    // TODO: Validation on search inputs
    console.log("filtering", filtering);
    console.log("queryString", queryString);
    if (filtering) {
      searchUrl = `${ROUTE_SEARCH}?${queryString}`;
      await locForSetRecentSearches(searchUrl);
    } else if (inputValue) {
      searchUrl = `${ROUTE_SEARCH}?title=${inputValue}`;
      await locForSetRecentSearches(searchUrl);
    } else {
      searchUrl = `${ROUTE_SEARCH}`;
    }

    nav(searchUrl);
  }, [filtering, nav, inputValue, queryString]);

  // Enter pressed: search if input is focused
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        await handleSearch();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSearch, inputValue, filtering, queryString]);

  // #endregion Search Inputs

  function handleInverseFilters(filter: SearchParamInverseFilter) {
    // update the inverse filters state, if it's already in the state, remove it
    // if not, add it to the state
    setInverseFilters((prev) => {
      if (prev.includes(filter)) {
        return prev.filter((f) => f !== filter);
      } else {
        return [...prev, filter];
      }
    });
  }

  // useEffect(() => {
  //   console.log(inverseFilters);
  // }, [inverseFilters]);

  return (
    <>
      <CommandGroup
        ref={advFilterRef}
        className="h-full w-full overflow-hidden"
      >
        <div className="grid grid-cols-[1fr_2fr] gap-y-3 p-1 w-full overflow-hidden">
          {/* Title */}
          <label
            htmlFor="edit-title-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center"
          >
            Title
          </label>
          <div className="flex items-center gap-2">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("title")}
              onChange={() => {
                handleInverseFilters("title");
              }}
            />
            <Input
              id="edit-title-search"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              maxLength={255}
            />
          </div>

          {/* Status */}
          <label
            htmlFor="edit-status-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center"
          >
            Status
          </label>
          <div className="flex items-center gap-2 w-full">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("status")}
              onChange={() => {
                handleInverseFilters("status");
              }}
              disabled={true}
            />
            {/* Status Dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className="w-full" asChild>
                <Button
                  id="edit-status-search"
                  variant={"outline"}
                  className="capitalize justify-between w-full"
                >
                  {status ? status : "Select status"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="bottom"
                align="end"
                className="dark:bg-background"
                ref={statusRef}
              >
                <DropdownMenuItem
                  onClick={() => {
                    setStatus("complete");
                  }}
                >
                  Completed
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setStatus("incomplete");
                  }}
                >
                  Incomplete
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setStatus("all");
                  }}
                >
                  All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Assigned To */}
          <label
            htmlFor="edit-assigned-to-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center"
          >
            Assigned To
          </label>
          {/* Assignee Dropdown */}
          <div className="flex items-center gap-2 w-full">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("assignedTo")}
              onChange={() => {
                handleInverseFilters("assignedTo");
              }}
            />
            <MultiPicker
              assignees={assignedTo}
              handleFieldAssignees={(id) =>
                handleAssigneeChange(id, setAssignedTo)
              }
              multiAssigneeRef={multiAssigneeRef}
              type="Assignee"
              containerClassName="w-full"
            />
          </div>

          {/* Due Date */}
          <label
            htmlFor="edit-due-date-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-start"
          >
            Due Date
          </label>
          <div className="flex items-center gap-2 w-full">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("dueDate")}
              onChange={() => {
                handleInverseFilters("dueDate");
              }}
            />
            <DatePickerSearch
              datePickerRef={dateDropdownRef}
              onDateTypeChange={(type, data) =>
                handleDateTypeChange(type, data, setDueDateType, setDueDateData)
              }
              dateType={dueDateType}
              setDateType={setDueDateType}
              dateData={dueDateData}
              setDateData={setDueDateData}
            />
          </div>

          {/* Tags */}
          <label
            htmlFor="edit-tags-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-start"
          >
            Tags
          </label>
          <div className="flex items-center gap-2 w-full">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("tag")}
              onChange={() => {
                handleInverseFilters("tag");
              }}
            />
            <CreateTaskTags
              id="edit-tags-search"
              taskTags={tagData}
              handleFieldTags={handleTagChange}
              align={isNotSmall ? "start" : undefined}
              side={isNotSmall ? "right" : undefined}
              createTagAllowed={false}
            />
          </div>

          {tagData.length > 0 && (
            <>
              <div></div>
              <MultiCheckbox<SearchParamsObj["tagType"]>
                searchTypes={SEARCH_TAG_TYPES}
                selectedType={tagType}
                setSelectedType={setTagType}
              />
            </>
          )}

          {/* Collaborators */}
          <label
            htmlFor="edit-collaborators-search"
            className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center"
          >
            Collaborators
          </label>
          <div className="flex items-center gap-2 w-full">
            <SearchBoxFilterInverseCheckbox
              inverseActive={inverseFilters.includes("collaborators")}
              onChange={() => {
                handleInverseFilters("collaborators");
              }}
            />
            <CollaboratorPicker
              collaborators={collaboratorInput}
              collaboratorRef={collaboratorRef}
              handleFieldCollaborators={handleCollaboratorChange}
              side="bottom"
              align="end"
              containerClassName="w-full"
            />
          </div>

          {collaboratorInput.length > 0 && (
            <>
              <div></div>
              <MultiCheckbox<SearchParamsObj["collaboratorSearchType"]>
                searchTypes={SEARCH_COLLABORATOR_TYPES}
                selectedType={collaboratorSearchType}
                setSelectedType={setCollaboratorSearchType}
              />
            </>
          )}

          {/* Dates */}
          {addedFilters.date &&
            addedFilters.date.map((date, index) => (
              <Fragment key={date + "_" + index}>
                <div className="flex justify-start items-start relative">
                  <div className="relative">
                    <label className="ps-1 pt-1 font-light text-gray-600 dark:text-gray-300 self">
                      {date}
                    </label>
                    {/* Remove Filter */}
                    <Button
                      variant={"ghost"}
                      onClick={() => {
                        handleAddFilter("date", date);
                      }}
                      size={"icon"}
                      className="ms-2 absolute -top-1 -right-1 translate-x-1/2 -translate-y-1/2 !px-0 !py-0 h-4 w-4"
                    >
                      <Cross2Icon
                        className="relative h-3 w-3"
                        aria-hidden="true"
                      />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full">
                  <SearchBoxFilterInverseCheckbox
                    inverseActive={inverseFilters.includes(
                      date === "Last Modified"
                        ? "modifiedDate"
                        : date === "Created Date"
                          ? "createdDate"
                          : "completedDate"
                    )}
                    onChange={() => {
                      handleInverseFilters(
                        date === "Last Modified"
                          ? "modifiedDate"
                          : date === "Created Date"
                            ? "createdDate"
                            : "completedDate"
                      );
                    }}
                  />

                  {date === "Last Modified" && (
                    <DatePickerSearch
                      datePickerRef={dateDropdownRef}
                      onDateTypeChange={(type, data) =>
                        handleDateTypeChange(
                          type,
                          data,
                          setModifiedDateType,
                          setModifiedDateData
                        )
                      }
                      dateType={modifiedDateType}
                      setDateType={setModifiedDateType}
                      dateData={modifiedDateData}
                      setDateData={setModifiedDateData}
                    />
                  )}

                  {date === "Created Date" && (
                    <DatePickerSearch
                      datePickerRef={dateDropdownRef}
                      onDateTypeChange={(type, data) => {
                        handleDateTypeChange(
                          type,
                          data,
                          setCreatedDateType,
                          setCreatedDateData
                        );
                      }}
                      dateType={createdDateType}
                      setDateType={setCreatedDateType}
                      dateData={createdDateData}
                      setDateData={setCreatedDateData}
                    />
                  )}

                  {date === "Completed Date" && (
                    <DatePickerSearch
                      datePickerRef={dateDropdownRef}
                      onDateTypeChange={(type, data) => {
                        handleDateTypeChange(
                          type,
                          data,
                          setCompletedDateType,
                          setCompletedDateData
                        );
                      }}
                      dateType={completedDateType}
                      setDateType={setCompletedDateType}
                      dateData={completedDateData}
                      setDateData={setCompletedDateData}
                    />
                  )}
                </div>
              </Fragment>
            ))}

          {/* People */}
          {addedFilters.people &&
            addedFilters.people.map((people, index) => (
              <Fragment key={people + "_" + index}>
                <div className="flex justify-start items-center">
                  {/* <label className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center">
                    {people}
                  </label>
                  <div>
                    <Button
                      variant={"ghost"}
                      onClick={() => {
                        handleAddFilter("people", people);
                      }}
                      className="ms-2"
                    >
                      <Cross2Icon
                        className="relative top-[1px] mr-1 h-3 w-3"
                        aria-hidden="true"
                      />
                    </Button>
                  </div> */}
                  <div className="relative">
                    <label className="ps-1 font-light text-gray-600 dark:text-gray-300 self-center">
                      {people}
                    </label>
                    {/* Remove Filter */}
                    <Button
                      variant={"ghost"}
                      onClick={() => {
                        handleAddFilter("people", people);
                      }}
                      size={"icon"}
                      className="ms-2 absolute -top-1 -right-1 translate-x-1/2 -translate-y-1/2 !px-0 !py-0 h-4 w-4"
                    >
                      <Cross2Icon
                        className="relative h-3 w-3"
                        aria-hidden="true"
                      />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full">
                  <SearchBoxFilterInverseCheckbox
                    inverseActive={inverseFilters.includes(
                      people === "Assigned by"
                        ? "assignedBy"
                        : people === "Created By"
                          ? "createdBy"
                          : people === "Updated By"
                            ? "updatedBy"
                            : "hasUsersTimer"
                    )}
                    onChange={() => {
                      handleInverseFilters(
                        people === "Assigned by"
                          ? "assignedBy"
                          : people === "Created By"
                            ? "createdBy"
                            : people === "Updated By"
                              ? "updatedBy"
                              : "hasUsersTimer"
                      );
                    }}
                  />
                  {people === "Assigned by" && (
                    <MultiPicker
                      assignees={assignedBy}
                      handleFieldAssignees={(id) =>
                        handleAssigneeChange(id, setAssignedBy)
                      }
                      multiAssigneeRef={multiAssigneeRef}
                      type="Assigned by"
                      containerClassName="w-full"
                    />
                  )}

                  {people === "Created By" && (
                    <MultiPicker
                      assignees={createdBy}
                      handleFieldAssignees={(id) =>
                        handleAssigneeChange(id, setCreatedBy)
                      }
                      multiAssigneeRef={multiAssigneeRef}
                      type="Created By"
                      containerClassName="w-full"
                    />
                  )}

                  {people === "Updated By" && (
                    <MultiPicker
                      assignees={updatedBy}
                      handleFieldAssignees={(id) =>
                        handleAssigneeChange(id, setUpdatedBy)
                      }
                      multiAssigneeRef={multiAssigneeRef}
                      type="Updated By"
                      containerClassName="w-full"
                    />
                  )}

                  {people === "Has Timer" && (
                    <MultiPicker
                      assignees={hasTimer}
                      handleFieldAssignees={(id) =>
                        handleAssigneeChange(id, setHasTimer)
                      }
                      multiAssigneeRef={multiAssigneeRef}
                      type="Has Timer"
                      containerClassName="w-full"
                    />
                  )}
                </div>
              </Fragment>
            ))}
        </div>
      </CommandGroup>
      <CommandSeparator />
      {/* Filter Buttons */}
      <div className="flex justify-between flex-row p-2" ref={buttonSectionRef}>
        <div>
          <AddFiltersSearch
            addFilterRef={addFilterRef}
            handleAddFilter={handleAddFilter}
          />
        </div>

        <div>
          <Button
            variant={"ghost"}
            onClick={() => {
              clearSearch();
            }}
            className="mr-2"
          >
            Clear
          </Button>

          <Button
            variant={"default"}
            className=""
            onClick={() => {
              handleSearch();
            }}
          >
            Search
          </Button>
        </div>
      </div>
    </>
  );
};

export default SearchBoxFilterAdvanced;
